import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useGetCountriesWithFilters } from '@/hooks/useGetCountriesWithFilters';

import { FilterKeys } from '../../components/Filters';
import { getCountriesPropVariables, MaFilterKeys } from './components/Filters/constants';

const MaOpportunitiesContext = createContext();

export const MA_OPPORTUNITIES_CONTEXT = 'MA_OPPORTUNITIES';

const INITIAL_VALUES = {
  [FilterKeys.assets]: [],
  [FilterKeys.basins]: [],
  [FilterKeys.countries]: [],
  [FilterKeys.companiesSeller]: [],
  [FilterKeys.includeHistoricalParticipants]: false,
  [FilterKeys.operator]: true,
  [FilterKeys.hydrocarbons]: [],
  [FilterKeys.shoreStatuses]: [],
  [FilterKeys.developmentStatuses]: [],
  [MaFilterKeys.valuation]: [],
  [MaFilterKeys.reserves]: [],
  [MaFilterKeys.production]: [],
  [MaFilterKeys.emissions]: [],
};

const getInitialFilters = () => {
  const sessionFilters = JSON.parse(sessionStorage.getItem(MA_OPPORTUNITIES_CONTEXT) || '{}');
  const filtersToLoad = [
    FilterKeys.assets,
    FilterKeys.basins,
    FilterKeys.countries,
    FilterKeys.companiesSeller,
  ];

  return {
    ...INITIAL_VALUES,
    ...filtersToLoad.reduce((acc, key) => {
      acc[key] = sessionFilters[key] || INITIAL_VALUES[key];
      return acc;
    }, {}),
  };
};

const MaOpportunitiesProvider = ({ children }) => {
  // start with no tab selected to avoid rendering the first tab by default
  const [currentTab, setCurrentTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [valuationRange, setValuationRange] = useState([]);
  const [reservesRange, setReservesRange] = useState([]);
  const [productionRange, setProductionRange] = useState([]);
  const [emissionsRange, setEmissionsRange] = useState([]);
  const [filters, setFilters] = useState(getInitialFilters());

  const { countries } = useGetCountriesWithFilters({
    variables: getCountriesPropVariables(filters),
  });

  useEffect(() => {
    // remove advanced filters before saving values to session
    const filtersToSave = {
      [FilterKeys.assets]: filters[FilterKeys.assets],
      [FilterKeys.basins]: filters[FilterKeys.basins],
      [FilterKeys.countries]: filters[FilterKeys.countries],
      [FilterKeys.companiesSeller]: filters[FilterKeys.companiesSeller],
      [FilterKeys.includeHistoricalParticipants]: filters[FilterKeys.includeHistoricalParticipants],
      [FilterKeys.operator]: filters[FilterKeys.operator],
    };

    sessionStorage.setItem(MA_OPPORTUNITIES_CONTEXT, JSON.stringify(filtersToSave));
  }, [filters]);

  const resetFilters = () => {
    setLoading(true);
    setFilters({
      ...INITIAL_VALUES,
      [MaFilterKeys.valuation]: valuationRange,
      [MaFilterKeys.reserves]: reservesRange,
      [MaFilterKeys.production]: productionRange,
      [MaFilterKeys.emissions]: emissionsRange,
    });
  };

  const setRegion = useCallback(
    region => {
      if (!countries || countries.length === 0) return;
      if (!region) return;

      const countriesFromRegion = countries.filter(c => c?.region?.displayName === region);
      if (countriesFromRegion.length > 0) {
        setFilters(prevFilters => ({
          ...prevFilters,
          [FilterKeys.countries]: countriesFromRegion,
        }));
      }
    },
    [countries],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: force loading state when filters change
  useEffect(() => {
    // Had to create a loading state to force the dateSlider component to re-render
    // when resetting the filters, otherwise the component was showing the old values,
    // even though the filters state was updated.
    // I think this is an issue with the library.
    // TODO: in the future, after updating MUI, we need to revisit this to see if it can be removed.
    setLoading(false);
  }, [filters]);

  const filterVariables = useMemo(() => {
    const [minValuation, maxValuation] = filters?.[MaFilterKeys.valuation] || [];
    const [minReserves, maxReserves] = filters?.[MaFilterKeys.reserves] || [];
    const [minProduction, maxProduction] = filters?.[MaFilterKeys.production] || [];
    const [minEmissions, maxEmissions] = filters?.[MaFilterKeys.emissions] || [];

    return {
      assets: filters[FilterKeys.assets].map(a => Number(a.id)),
      countries: filters[FilterKeys.countries]?.map(c => c.isoCode) || [],
      sellers: filters[FilterKeys.companiesSeller]?.map(s => Number(s.id)) || [],
      basins: filters[FilterKeys.basins]?.map(b => Number(b.id)) || [],
      hydrocarbonTypes: [...new Set(filters[FilterKeys.hydrocarbons]?.map(h => h.type))] || [],
      oilTypes: [...new Set(filters[FilterKeys.hydrocarbons]?.map(h => h.oil))] || [],
      developmentStatuses: filters[FilterKeys.developmentStatuses]?.map(d => Number(d.id)) || [],
      shoreStatuses: filters[FilterKeys.shoreStatuses]?.map(s => Number(s.id)) || [],
      // Only filter if the value is different from the default
      minValuation: minValuation !== valuationRange[0] ? minValuation : null,
      maxValuation: maxValuation !== valuationRange[1] ? maxValuation : null,
      minReserves: minReserves !== reservesRange[0] ? minReserves : null,
      maxReserves: maxReserves !== reservesRange[1] ? maxReserves : null,
      minProduction: minProduction !== productionRange[0] ? minProduction : null,
      maxProduction: maxProduction !== productionRange[1] ? maxProduction : null,
      minEmissions: minEmissions !== emissionsRange[0] ? minEmissions : null,
      maxEmissions: maxEmissions !== emissionsRange[1] ? maxEmissions : null,
    };
  }, [filters, valuationRange, reservesRange, productionRange, emissionsRange]);

  return (
    <MaOpportunitiesContext.Provider
      value={{
        currentTab,
        setCurrentTab,
        filters,
        setFilters,
        setRegion,
        resetFilters,
        valuationRange: valuationRange,
        setValuationRange: setValuationRange,
        reservesRange,
        setReservesRange,
        productionRange,
        setProductionRange,
        emissionsRange,
        setEmissionsRange,
        loading,
        setLoading,
        filterVariables,
      }}
    >
      {children}
    </MaOpportunitiesContext.Provider>
  );
};

const useMaOpportunitiesContext = () => useContext(MaOpportunitiesContext);

export { MaOpportunitiesProvider, useMaOpportunitiesContext };
